import { useEffect, useMemo, useState } from 'react';
import {
  EXPORTER_COST_MAPPINGS_CHANGED_EVENT,
  ExporterCostCategory,
  getCustomCostCategoriesEnabled,
  getExporterCostCategories,
  getSettlementExporterCosts,
  SettlementExporterCost,
  UNCLASSIFIED_EXPORTER_COST_LABEL,
} from 'common/src/api/exporter-cost-categories';

export type CostStandard = 'fm' | 'exporter';

export interface ExporterCostCategoryTotal {
  categoryId: number | null;
  name: string;
  amountLocal: number;
  amountUsd: number;
}

/**
 * Loads the exporter cost view for the given settlements. `available` is true
 * only when the exporter has custom categories enabled and at least one of the
 * settlements is in an exporter-view season (the RPC returns no rows
 * otherwise).
 */
export const useExporterCostView = (
  exporterId: number | null | undefined,
  settlementIds: number[]
) => {
  const [categories, setCategories] = useState<ExporterCostCategory[]>([]);
  const [costs, setCosts] = useState<SettlementExporterCost[]>([]);
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const idsKey = settlementIds.join(',');

  // Mappings apply retroactively: reload when any is saved.
  useEffect(() => {
    const reload = () => setReloadKey((key) => key + 1);
    window.addEventListener(EXPORTER_COST_MAPPINGS_CHANGED_EVENT, reload);
    return () =>
      window.removeEventListener(EXPORTER_COST_MAPPINGS_CHANGED_EVENT, reload);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setCategories([]);
    setCosts([]);
    if (!exporterId || settlementIds.length === 0) return;

    setLoading(true);
    (async () => {
      if (!(await getCustomCostCategoriesEnabled(exporterId))) return;
      const [nextCategories, nextCosts] = await Promise.all([
        getExporterCostCategories(exporterId, true),
        getSettlementExporterCosts(settlementIds),
      ]);
      if (!cancelled) {
        setCategories(nextCategories);
        setCosts(nextCosts);
      }
    })()
      .catch((error) => {
        console.error('Error loading exporter cost view:', error);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // idsKey stands in for settlementIds' contents.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exporterId, idsKey, reloadKey]);

  const categoryTotals = useMemo<ExporterCostCategoryTotal[]>(() => {
    const byCategory = new Map<number | null, ExporterCostCategoryTotal>();
    costs.forEach((cost) => {
      const current = byCategory.get(cost.categoryId) || {
        categoryId: cost.categoryId,
        name:
          categories.find((category) => category.id === cost.categoryId)
            ?.name || UNCLASSIFIED_EXPORTER_COST_LABEL,
        amountLocal: 0,
        amountUsd: 0,
      };
      current.amountLocal += cost.amountLocal;
      current.amountUsd += cost.amountUsd;
      byCategory.set(cost.categoryId, current);
    });

    const order = (categoryId: number | null) =>
      categoryId === null
        ? Number.MAX_SAFE_INTEGER
        : categories.findIndex((category) => category.id === categoryId);
    return Array.from(byCategory.values()).sort(
      (a, b) => order(a.categoryId) - order(b.categoryId)
    );
  }, [categories, costs]);

  /** Total exporter-view cost in USD per settlement detail id. */
  const detailCostsUsd = useMemo(() => {
    const totals: Record<number, number> = {};
    costs.forEach((cost) => {
      totals[cost.settlementDetailId] =
        (totals[cost.settlementDetailId] || 0) + cost.amountUsd;
    });
    return totals;
  }, [costs]);

  return {
    available: costs.length > 0,
    loading,
    categories,
    costs,
    categoryTotals,
    detailCostsUsd,
  };
};
